import { jsxs, jsx } from 'react/jsx-runtime';
import { useReducer, useEffect, createRef, useState, useMemo } from 'react';
import { Banner, BannerImageContainer, BannerImage, FluidImage, BannerTextContainer, Box, Typography, StandardForm, FormField, InputWithFloatingLabel, TextInput, FloatingLabel, FormError, GridContainer, Spinner, Row, Link, Button } from '@vp/swan';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var f = Object.defineProperty;
var c = (n, e, t) => e in n ? f(n, e, { enumerable: true, configurable: true, writable: true, value: t }) : n[e] = t;
var l = (n, e, t) => c(n, typeof e != "symbol" ? e + "" : e, t);
function h() {
  const n = "en-US";
  if (typeof navigator > "u")
    return n;
  const e = navigator;
  if (e) {
    if (e.language)
      return e.language;
    if (e.languages && e.languages[0])
      return e.languages[0];
    if ("userLanguage" in e)
      return e.userLanguage;
    if ("browserLanguage" in e)
      return e.browserLanguage;
  }
  return n;
}
function _(n, e) {
  if (e[n]) return n;
  const t = n.indexOf("-"), a = t >= 0 ? n.substring(0, t) : n;
  return e[a] ? a : Object.keys(e)[0];
}
function d(n) {
  const e = [
    "_interfaceLanguage",
    "_language",
    "_defaultLanguage",
    "_defaultLanguageFirstLevelKeys",
    "_props"
  ];
  n.forEach((t) => {
    if (e.indexOf(t) !== -1)
      throw new Error(`${t} cannot be used as a key. It is a reserved word.`);
  });
}
function L(n) {
  let e = "";
  const t = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for (let a = 0; a < n; a += 1)
    e += t.charAt(Math.floor(Math.random() * t.length));
  return e;
}
const r = /(\{[\d|\w]+\})/, o = /(\$ref\{[\w|.]+\})/;
class b {
  /**
   * Constructor used to provide the strings objects in various language and the optional callback to get
   * the interface language
   * @param props - the strings object
   * @param options - configuration options
   */
  constructor(e, t) {
    l(this, "_opts");
    l(this, "_interfaceLanguage");
    l(this, "_language");
    l(this, "_defaultLanguage");
    l(this, "_defaultLanguageFirstLevelKeys");
    l(this, "_props");
    l(this, "_availableLanguages");
    typeof t == "function" && (t = { customLanguageInterface: t }), this._opts = {
      customLanguageInterface: h,
      pseudo: false,
      pseudoMultipleLanguages: false,
      logsEnabled: true,
      ...t
    }, this._interfaceLanguage = this._opts.customLanguageInterface(), this._language = this._interfaceLanguage, this.setContent(e);
  }
  /**
   * Set the strings objects based on the parameter passed in the constructor
   */
  setContent(e) {
    const [t] = Object.keys(e);
    this._defaultLanguage = t, this._defaultLanguageFirstLevelKeys = [], this._props = e, d(Object.keys(e[this._defaultLanguage])), Object.keys(this._props[this._defaultLanguage]).forEach((a) => {
      typeof this._props[this._defaultLanguage][a] == "string" && this._defaultLanguageFirstLevelKeys.push(a);
    }), this.setLanguage(this._interfaceLanguage), this._opts.pseudo && this._pseudoAllValues(this._props);
  }
  /**
   * Replace all strings to pseudo value
   */
  _pseudoAllValues(e) {
    Object.keys(e).forEach((t) => {
      if (typeof e[t] == "object")
        this._pseudoAllValues(e[t]);
      else if (typeof e[t] == "string") {
        if (e[t].indexOf("[") === 0 && e[t].lastIndexOf("]") === e[t].length - 1)
          return;
        const a = e[t].split(" ");
        for (let s = 0; s < a.length; s += 1) {
          if (a[s].match(r) || a[s].match(o))
            continue;
          let i = a[s].length;
          this._opts.pseudoMultipleLanguages && (i = Math.floor(i * 1.4)), a[s] = L(i);
        }
        e[t] = `[${a.join(" ")}]`;
      }
    });
  }
  /**
   * Can be used from outside the class to force a particular language
   * independently from the interface one
   */
  setLanguage(e) {
    const t = _(e, this._props), a = Object.keys(this._props)[0];
    if (this._language = t, this._props[t]) {
      for (const i of this._defaultLanguageFirstLevelKeys)
        delete this[i];
      let s = { ...this._props[this._language] };
      Object.keys(s).forEach((i) => {
        this[i] = s[i];
      }), a !== this._language && (s = this._props[a], this._fallbackValues(s, this));
    }
  }
  /**
   * Load fallback values for missing translations
   */
  _fallbackValues(e, t) {
    Object.keys(e).forEach((a) => {
      Object.prototype.hasOwnProperty.call(e, a) && !t[a] && t[a] !== "" ? (t[a] = e[a], this._opts.logsEnabled && console.log(
        `🚧 👷 key '${a}' not found in localizedStrings for language ${this._language} 🚧`
      )) : typeof t[a] != "string" && this._fallbackValues(e[a], t[a]);
    });
  }
  getLanguage() {
    return this._language;
  }
  getInterfaceLanguage() {
    return this._interfaceLanguage;
  }
  getAvailableLanguages() {
    return this._availableLanguages || (this._availableLanguages = Object.keys(this._props)), this._availableLanguages;
  }
  formatString(e, ...t) {
    let a = e || "";
    return typeof a == "string" && (a = this.getString(e, null, true) || a), a.split(o).filter(Boolean).map((i) => {
      if (i.match(o)) {
        const g = i.slice(5, -1), u = this.getString(g);
        return u || (this._opts.logsEnabled && console.log(
          `No Localization ref found for '${i}' in string '${e}'`
        ), `$ref(id:${g})`);
      }
      return i;
    }).join("").split(r).filter(Boolean).map((i) => {
      if (i.match(r)) {
        const g = i.slice(1, -1);
        let u = t[g];
        return u === void 0 && t[0] && (u = t[0][g]), u;
      }
      return i;
    }).join("");
  }
  getString(e, t, a = false) {
    try {
      let s = this._props[t || this._language];
      const i = e.split(".");
      for (const g of i) {
        if (s[g] === void 0)
          throw new Error(g);
        s = s[g];
      }
      return s;
    } catch (s) {
      !a && this._opts.logsEnabled && console.log(
        `No localization found for key '${e}' and language '${t}', failed on ${s.message}`
      );
    }
    return null;
  }
  getContent() {
    return this._props;
  }
}

var defaultLocale = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: "It's good to be on the list!!1!. (default language)",
    signUpPopSubcopy: 'Get {{discount}}% off your order when you sign up for our emails',
    signUpPopDisclaimer: "Yes, I'd like to receive special offer emails from VistaPrint, as well as news about products, services and my designs in progress. Read our {{signUpPopDisclaimerPrivacyLink}}.",
    signUpPopDisclaimerPrivacyLink: 'Privacy and Cookie policy',
    signUpPopInputPlaceholder: 'Subscription email',
    signUpPopInvalidEmailMessage: 'Invalid email address. Example email: me@email.com',
    signUpPopCallToActionButton: 'Submit',
    signUpPopCancelButton: 'No thanks',
    signUpPopLoadingCoupon: 'Loading',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: "It's good to be on the list!!1!. (default language)",
    signUpPopSubcopyMobile: 'Get {{discount}}% off your order when you sign up for our emails',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: "Thanks – you're on the list!",
    signUpConfirmationSubcopy: '(And your promo code is on its way.)',
    signUpConfirmationBodyCopy: 'Check your email – you will receive the promo code in your inbox.*',
    signUpConfirmationDisclaimer: '*Offer expires {{expirationDate}}. You can only use one code per order at checkout. Unless stated otherwise, this code is not valid in combination with other offers or on delivery, product options, design services, digital products or the Promotique site. We reserve the right to change, cancel and limit any offer.',
    signUpConfirmationOkButton: 'Ok',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oops! Something went wrong.',
    signUpErrorBodyCopy: 'We were unable to sign you up for our emails. Please try again later or contact our customer care team for assistance.'
};

var daDK = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Det er godt at være på listen.',
    signUpPopSubcopy: 'Få {{discount}} % rabat på din ordre, når du tilmelder dig vores e-mails.',
    signUpPopDisclaimer: 'Ja, jeg vil gerne modtage særlige tilbudsmails fra VistaPrint, samt nyheder vedrørende produkter, tjenester og design under udarbejdelse. Læs vores {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: 'Privatlivs- og cookiepolitik',
    signUpPopInputPlaceholder: 'Abonnementsmail',
    signUpPopInvalidEmailMessage: 'Ugyldig e-mailadresse. Eksempel på e-mail: mig@email.com',
    signUpPopCallToActionButton: 'Send',
    signUpPopCancelButton: 'Nej tak',
    signUpPopLoadingCoupon: 'Indlæser',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Det er godt at være på listen.',
    signUpPopSubcopyMobile: 'Få {{discount}} % rabat på din ordre, når du tilmelder dig vores e-mails.',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Tak – du er skrevet på listen!',
    signUpConfirmationSubcopy: '(Og din kampagnekode er på vej.)',
    signUpConfirmationBodyCopy: 'Tjek din e-mail – du modtager kampagnekoden i din indbakke.*',
    signUpConfirmationDisclaimer: '*Tilbuddet udløber {{expirationDate}}. Du kan kun bruge én kode pr. ordre ved kassen. Medmindre andet er angivet, gælder koden ikke i kombination med andre rabatter, på levering, produktmuligheder, designservice, digitale produkter eller Promotique-hjemmesiden. Vi forbeholder os retten til at ændre, annullere eller begrænse enhver rabat.',
    signUpConfirmationOkButton: 'Okay',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Ups! Der opstod en fejl.',
    signUpErrorBodyCopy: 'Vi kunne ikke tilmelde dig vores e-mails. Kontakt kundeservice for at få hjælp, eller prøv igen senere.'
};

var deAT = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Anmelden lohnt sich',
    signUpPopSubcopy: 'Denn Sie erhalten {{discount}} % Rabatt auf Ihre Bestellung',
    signUpPopDisclaimer: 'Ja, ich möchte Sonderangebote von VistaPrint sowie Neuigkeiten über Produkte, Dienstleistungen und meine aktuellen Designs per E-Mail erhalten. Lesen Sie unsere {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: 'Datenschutz- und Cookie-Richtlinie',
    signUpPopInputPlaceholder: 'Ihre E-Mail-Adresse',
    signUpPopInvalidEmailMessage: 'Ungültige E-Mail-Adresse. Beispiel: ich@email.com',
    signUpPopCallToActionButton: 'Senden',
    signUpPopCancelButton: 'Nein, danke',
    signUpPopLoadingCoupon: 'Ladevorgang',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Anmelden lohnt sich',
    signUpPopSubcopyMobile: 'Denn Sie erhalten {{discount}} % Rabatt auf Ihre Bestellung',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Vielen Dank! Wir haben Sie auf unsere Verteilerliste gesetzt.',
    signUpConfirmationSubcopy: 'Ihr Angebotscode ist auf dem Weg.',
    signUpConfirmationBodyCopy: 'Er sollte schon bald in Ihrem Posteingang ankommen.*',
    signUpConfirmationDisclaimer: '* Das Angebot ist bis {{expirationDate}} gültig. Pro Bestellung kann nur ein Code eingelöst werden. Sofern nicht anders angegeben, kann dieser Code weder in Kombination mit anderen Angeboten noch auf die Liefergebühren, Designservices, digitale Produkte oder auf der Promotique-Seite angewendet werden. Wir behalten uns das Recht auf Änderung, Widerruf und Einschränkung jedes beliebigen Angebots vor.',
    signUpConfirmationOkButton: 'OK',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Hoppla! Es ist ein Fehler aufgetreten.',
    signUpErrorBodyCopy: 'Leider war es nicht möglich, Ihre Anmeldung für unseren Newsletter abzuschließen. Bitte versuchen Sie es später noch einmal oder kontaktieren Sie unseren Kundenservice.'
};

var deCH = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Anmelden lohnt sich',
    signUpPopSubcopy: 'Denn Sie erhalten {{discount}} % Rabatt auf Ihre Bestellung',
    signUpPopDisclaimer: 'Ja, ich möchte Sonderangebote von VistaPrint sowie Neuigkeiten über Produkte, Dienstleistungen und meine aktuellen Designs per E-Mail erhalten. Lesen Sie unsere {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: 'Datenschutz- und Cookie-Richtlinie',
    signUpPopInputPlaceholder: 'Ihre E-Mail-Adresse',
    signUpPopInvalidEmailMessage: 'Ungültige E-Mail-Adresse. Beispiel: ich@email.com',
    signUpPopCallToActionButton: 'Senden',
    signUpPopCancelButton: 'Nein, danke',
    signUpPopLoadingCoupon: 'Ladevorgang',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Anmelden lohnt sich',
    signUpPopSubcopyMobile: 'Denn Sie erhalten {{discount}} % Rabatt auf Ihre Bestellung',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Vielen Dank! Wir haben Sie auf unsere Verteilerliste gesetzt.',
    signUpConfirmationSubcopy: 'Ihr Angebotscode ist auf dem Weg.',
    signUpConfirmationBodyCopy: 'Er sollte schon bald in Ihrem Posteingang ankommen.*',
    signUpConfirmationDisclaimer: '* Das Angebot ist bis {{expirationDate}} gültig. Pro Bestellung kann nur ein Code eingelöst werden. Sofern nicht anders angegeben, kann dieser Code weder in Kombination mit anderen Angeboten noch auf die Liefergebühren, Designservices, digitale Produkte oder auf der Promotique-Seite angewendet werden. Wir behalten uns das Recht auf Änderung, Widerruf und Einschränkung jedes beliebigen Angebots vor.',
    signUpConfirmationOkButton: 'OK',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Hoppla! Es ist ein Fehler aufgetreten.',
    signUpErrorBodyCopy: 'Leider war es nicht möglich, Ihre Anmeldung für unseren Newsletter abzuschließen. Bitte versuchen Sie es später noch einmal oder kontaktieren Sie unseren Kundenservice.'
};

var deDE = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Anmelden lohnt sich',
    signUpPopSubcopy: 'Denn Sie erhalten {{discount}} % Rabatt auf Ihre Bestellung',
    signUpPopDisclaimer: 'Ja, ich möchte Sonderangebote von VistaPrint sowie Neuigkeiten über Produkte, Dienstleistungen und meine aktuellen Designs per E-Mail erhalten. Lesen Sie unsere {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: 'Datenschutz- und Cookie-Richtlinie',
    signUpPopInputPlaceholder: 'Ihre E-Mail-Adresse',
    signUpPopInvalidEmailMessage: 'Ungültige E-Mail-Adresse. Beispiel: ich@email.com',
    signUpPopCallToActionButton: 'Senden',
    signUpPopCancelButton: 'Nein, danke',
    signUpPopLoadingCoupon: 'Ladevorgang',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Anmelden lohnt sich',
    signUpPopSubcopyMobile: 'Denn Sie erhalten {{discount}} % Rabatt auf Ihre Bestellung',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Vielen Dank! Wir haben Sie auf unsere Verteilerliste gesetzt.',
    signUpConfirmationSubcopy: 'Ihr Angebotscode ist auf dem Weg.',
    signUpConfirmationBodyCopy: 'Er sollte schon bald in Ihrem Posteingang ankommen.*',
    signUpConfirmationDisclaimer: '* Das Angebot ist bis {{expirationDate}} gültig. Pro Bestellung kann nur ein Code eingelöst werden. Sofern nicht anders angegeben, kann dieser Code weder in Kombination mit anderen Angeboten noch auf die Liefergebühren, Designservices, digitale Produkte oder auf der Promotique-Seite angewendet werden. Wir behalten uns das Recht auf Änderung, Widerruf und Einschränkung jedes beliebigen Angebots vor.',
    signUpConfirmationOkButton: 'OK',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Hoppla! Es ist ein Fehler aufgetreten.',
    signUpErrorBodyCopy: 'Leider war es nicht möglich, Ihre Anmeldung für unseren Newsletter abzuschließen. Bitte versuchen Sie es später noch einmal oder kontaktieren Sie unseren Kundenservice.'
};

var enAU = {
    __generalComment: 'This file is set up manually and not through the usual translation flow',
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: "It's good to be on the list",
    signUpPopSubcopy: 'Get our latest offers & deals (like {{discount}}% off your first order) when you sign up for our emails.',
    signUpPopDisclaimer: "Receive special offer emails from VistaPrint, as well as news about products, services and designs in progress. Discount only applicable to a customer's first order. Read our {{signUpPopDisclaimerPrivacyLink}}.",
    signUpPopDisclaimerPrivacyLink: 'Privacy and Cookie policy',
    signUpPopInputPlaceholder: 'Subscription email',
    signUpPopInvalidEmailMessage: 'Invalid email address. Example email: me@email.com',
    signUpPopCallToActionButton: 'Submit',
    signUpPopCancelButton: 'No thanks',
    signUpPopLoadingCoupon: 'Loading',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: "It's good to be on the list",
    signUpPopSubcopyMobile: 'Get our latest offers & deals (like {{discount}}% off your first order) when you sign up for our emails.',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: "Thanks – you're on the list!",
    signUpConfirmationSubcopy: '',
    signUpConfirmationBodyCopy: 'You’ll soon receive an email with your discount code - this may take a few minutes. You’ll never miss a special offer, now that you’re getting updates direct to your inbox.',
    signUpConfirmationDisclaimer: '',
    signUpConfirmationOkButton: 'Ok',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oops! Something went wrong.',
    signUpErrorBodyCopy: 'We were unable to sign you up for our emails. Please try again later or contact our customer care team for assistance.'
};

var enCA = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: "It's good to be on the list.",
    signUpPopSubcopy: 'Get {{discount}}% off your order when you sign up for our emails',
    signUpPopDisclaimer: "Yes, I'd like to receive special offer emails from VistaPrint, as well as news about products, services and my designs in progress. Read our {{signUpPopDisclaimerPrivacyLink}}.",
    signUpPopDisclaimerPrivacyLink: 'Privacy and Cookie policy',
    signUpPopInputPlaceholder: 'Subscription email',
    signUpPopInvalidEmailMessage: 'Invalid email address. Example email: me@email.com',
    signUpPopCallToActionButton: 'Submit',
    signUpPopCancelButton: 'No thanks',
    signUpPopLoadingCoupon: 'Loading',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: "It's good to be on the list.",
    signUpPopSubcopyMobile: 'Get {{discount}}% off your order when you sign up for our emails',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: "Thanks – you're on the list!",
    signUpConfirmationSubcopy: '(And your promo code is on its way.)',
    signUpConfirmationBodyCopy: 'Check your email – you will receive the promo code in your inbox.*',
    signUpConfirmationDisclaimer: '*Offer expires {{expirationDate}}. You can only use one code per order at checkout. Unless stated otherwise, this code is not valid in combination with other offers or on delivery, product options, design services, digital products or the Promotique site. We reserve the right to change, cancel and limit any offer.',
    signUpConfirmationOkButton: 'Ok',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oops! Something went wrong.',
    signUpErrorBodyCopy: 'We were unable to sign you up for our emails. Please try again later or contact our customer care team for assistance.'
};

var enGB = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: "It's good to be on the list.",
    signUpPopSubcopy: 'Get {{discount}}% off your order when you sign up for our emails',
    signUpPopDisclaimer: "Yes, I'd like to receive special offer emails from VistaPrint, as well as news about products, services and my designs in progress. Read our {{signUpPopDisclaimerPrivacyLink}}.",
    signUpPopDisclaimerPrivacyLink: 'Privacy and Cookie policy',
    signUpPopInputPlaceholder: 'Subscription email',
    signUpPopInvalidEmailMessage: 'Invalid email address. Example email: me@email.com',
    signUpPopCallToActionButton: 'Submit',
    signUpPopCancelButton: 'No thanks',
    signUpPopLoadingCoupon: 'Loading',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: "It's good to be on the list.",
    signUpPopSubcopyMobile: 'Get {{discount}}% off your order when you sign up for our emails',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: "Thanks – you're on the list!",
    signUpConfirmationSubcopy: '(And your promo code is on its way.)',
    signUpConfirmationBodyCopy: 'Check your email – you will receive the promo code in your inbox.*',
    signUpConfirmationDisclaimer: '*Offer expires {{expirationDate}}. You can only use one code per order at checkout. Unless stated otherwise, this code is not valid in combination with other offers or on delivery, product options, design services, digital products or the Promotique site. We reserve the right to change, cancel and limit any offer.',
    signUpConfirmationOkButton: 'Ok',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oops! Something went wrong.',
    signUpErrorBodyCopy: 'We were unable to sign you up for our emails. Please try again later or contact our customer care team for assistance.'
};

var enIE = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: "It's good to be on the list.",
    signUpPopSubcopy: 'Get {{discount}}% off your order when you sign up for our emails',
    signUpPopDisclaimer: "Yes, I'd like to receive special offer emails from VistaPrint, as well as news about products, services and my designs in progress. Read our {{signUpPopDisclaimerPrivacyLink}}.",
    signUpPopDisclaimerPrivacyLink: 'Privacy and Cookie policy',
    signUpPopInputPlaceholder: 'Subscription email',
    signUpPopInvalidEmailMessage: 'Invalid email address. Example email: me@email.com',
    signUpPopCallToActionButton: 'Submit',
    signUpPopCancelButton: 'No thanks',
    signUpPopLoadingCoupon: 'Loading',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: "It's good to be on the list.",
    signUpPopSubcopyMobile: 'Get {{discount}}% off your order when you sign up for our emails',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: "Thanks – you're on the list!",
    signUpConfirmationSubcopy: '(And your promo code is on its way.)',
    signUpConfirmationBodyCopy: 'Check your email – you will receive the promo code in your inbox.*',
    signUpConfirmationDisclaimer: '*Offer expires {{expirationDate}}. You can only use one code per order at checkout. Unless stated otherwise, this code is not valid in combination with other offers or on delivery, product options, design services, digital products or the Promotique site. We reserve the right to change, cancel and limit any offer.',
    signUpConfirmationOkButton: 'Ok',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oops! Something went wrong.',
    signUpErrorBodyCopy: 'We were unable to sign you up for our emails. Please try again later or contact our customer care team for assistance.'
};

var enIN = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: "It's good to be on the list.",
    signUpPopSubcopy: 'Get {{discount}}% off* your first order when you sign up for our emails',
    signUpPopDisclaimer: "Yes, I'd like to receive special offer emails from VistaPrint, as well as news about products, services and my designs in progress. Read our {{signUpPopDisclaimerPrivacyLink}}.",
    signUpPopDisclaimerPrivacyLink: 'Privacy and Cookie policy',
    signUpPopInputPlaceholder: 'Subscription email',
    signUpPopInvalidEmailMessage: 'Invalid email address. Example email: me@email.com',
    signUpPopCallToActionButton: 'Submit',
    signUpPopCancelButton: 'No thanks',
    signUpPopLoadingCoupon: 'Loading',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: "It's good to be on the list.",
    signUpPopSubcopyMobile: 'Get {{discount}}% off your order when you sign up for our emails',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: "Thanks – you're on the list!",
    signUpConfirmationSubcopy: '(And your promo code is on its way.)',
    signUpConfirmationBodyCopy: 'Check your email – you will receive the promo code in your inbox.*',
    signUpConfirmationDisclaimer: '*Offer expires {{expirationDate}}. You can only use one code per order at checkout. Unless stated otherwise, this code is not valid in combination with other offers or on delivery, product options, design services, digital products or the Promotique site. We reserve the right to change, cancel and limit any offer.',
    signUpConfirmationOkButton: 'Ok',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oops! Something went wrong.',
    signUpErrorBodyCopy: 'We were unable to sign you up for our emails. Please try again later or contact our customer care team for assistance.'
};

var enNZ = {
    __generalComment: 'This file is set up manually and not through the usual translation flow',
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Are you missing out?',
    signUpPopSubcopy: 'Get our latest offers & deals when you sign up for our emails',
    signUpPopDisclaimer: "Yes, I'd like to receive special offer emails from VistaPrint, as well as news about products, services and my designs in progress. Read our {{signUpPopDisclaimerPrivacyLink}}.",
    signUpPopDisclaimerPrivacyLink: 'Privacy and Cookie policy',
    signUpPopInputPlaceholder: 'Subscription email',
    signUpPopInvalidEmailMessage: 'Invalid email address. Example email: me@email.com',
    signUpPopCallToActionButton: 'Submit',
    signUpPopCancelButton: 'No thanks',
    signUpPopLoadingCoupon: 'Loading',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Are you missing out?',
    signUpPopSubcopyMobile: 'Get our latest offers & deals when you sign up for our emails',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: "Thanks – you're on the list!",
    signUpConfirmationSubcopy: '',
    signUpConfirmationBodyCopy: 'You’ll never miss a special offer, now that you’re getting updates direct to your inbox.',
    signUpConfirmationDisclaimer: '',
    signUpConfirmationOkButton: 'Ok',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oops! Something went wrong.',
    signUpErrorBodyCopy: 'We were unable to sign you up for our emails. Please try again later or contact our customer care team for assistance.'
};

var enSG = {
    __generalComment: 'This file is set up manually and not through the usual translation flow',
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Are you missing out?',
    signUpPopSubcopy: 'Get our latest offers & deals when you sign up for our emails',
    signUpPopDisclaimer: "Yes, I'd like to receive special offer emails from VistaPrint, as well as news about products, services and my designs in progress. Read our {{signUpPopDisclaimerPrivacyLink}}.",
    signUpPopDisclaimerPrivacyLink: 'Privacy and Cookie policy',
    signUpPopInvalidEmailMessage: 'Invalid email address. Example email: me@email.com',
    signUpPopInputPlaceholder: 'Subscription email',
    signUpPopCallToActionButton: 'Submit',
    signUpPopCancelButton: 'No thanks',
    signUpPopLoadingCoupon: 'Loading',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Are you missing out?',
    signUpPopSubcopyMobile: 'Get our latest offers & deals when you sign up for our emails',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: "Thanks – you're on the list!",
    signUpConfirmationSubcopy: '',
    signUpConfirmationBodyCopy: 'You’ll never miss a special offer, now that you’re getting updates direct to your inbox.',
    signUpConfirmationDisclaimer: '',
    signUpConfirmationOkButton: 'Ok',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oops! Something went wrong.',
    signUpErrorBodyCopy: 'We were unable to sign you up for our emails. Please try again later or contact our customer care team for assistance.'
};

var enUS = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: "It's good to be on the list.",
    signUpPopSubcopy: 'Get {{discount}}% off your order when you sign up for our emails',
    signUpPopDisclaimer: "Yes, I'd like to receive special offer emails from VistaPrint, as well as news about products, services and my designs in progress. Read our {{signUpPopDisclaimerPrivacyLink}}.",
    signUpPopDisclaimerPrivacyLink: 'Privacy and Cookie policy',
    signUpPopInputPlaceholder: 'Subscription email',
    signUpPopInvalidEmailMessage: 'Invalid email address. Example email: me@email.com',
    signUpPopCallToActionButton: 'Submit',
    signUpPopCancelButton: 'No thanks',
    signUpPopLoadingCoupon: 'Loading',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: "It's good to be on the list.",
    signUpPopSubcopyMobile: 'Get {{discount}}% off your order when you sign up for our emails',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: "Thanks – you're on the list!",
    signUpConfirmationSubcopy: '(And your promo code is on its way.)',
    signUpConfirmationBodyCopy: 'Check your email – you will receive the promo code in your inbox.*',
    signUpConfirmationDisclaimer: '*Offer expires {{expirationDate}}. You can only use one code per order at checkout. Unless stated otherwise, this code is not valid in combination with other offers or on delivery, product options, design services, digital products or the Promotique site. We reserve the right to change, cancel and limit any offer.',
    signUpConfirmationOkButton: 'Ok',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oops! Something went wrong.',
    signUpErrorBodyCopy: 'We were unable to sign you up for our emails. Please try again later or contact our customer care team for assistance.'
};

var esES = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: '¿Quieres disfrutar de las ventajas de suscribirse?',
    signUpPopSubcopy: 'Consigue un -{{discount}}% en tu pedido al registrarte para recibir nuestros correos electrónicos',
    signUpPopDisclaimer: 'Sí, quiero recibir ofertas especiales de VistaPrint, así como noticias sobre productos, servicios y mis diseños en curso. Consulta nuestra {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: 'Política de privacidad y cookies',
    signUpPopInputPlaceholder: 'Su correo electrónico',
    signUpPopInvalidEmailMessage: 'La dirección de correo electrónico no es válida. Ejemplo de dirección de correo electrónico: yo@correoelectronico.com',
    signUpPopCallToActionButton: 'Enviar',
    signUpPopCancelButton: 'No, gracias.',
    signUpPopLoadingCoupon: 'Cargando',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: '¿Quieres disfrutar de las ventajas de suscribirse?',
    signUpPopSubcopyMobile: 'Consigue un -{{discount}}% en tu pedido al registrarte para recibir nuestros correos electrónicos',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: '¡Gracias, ya estás suscrito!',
    signUpConfirmationSubcopy: '(Y tu código promocional está de camino).',
    signUpConfirmationBodyCopy: 'Te enviaremos el código promocional por correo electrónico.*',
    signUpConfirmationDisclaimer: '*Oferta válida hasta el {{expirationDate}}. Solo se puede usar un código por pedido en el proceso de tramitación. A menos que se indique lo contrario, este código no es válido en combinación con otras ofertas o aplicado a los gastos de envío, en opciones de producto, servicios de diseño, productos digitales o en la web de Promotique. Nos reservamos el derecho de modificar, cancelar y limitar cualquier oferta.',
    signUpConfirmationOkButton: 'Aceptar',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: '¡Vaya! Se ha producido un error.',
    signUpErrorBodyCopy: 'No hemos podido añadirte a nuestra lista de correo. Inténtalo de nuevo más tarde o contacta con el Servicio de Atención al cliente para que te ayuden.'
};

var esUS = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Ser parte de la lista se siente bien.',
    signUpPopSubcopy: 'Obtenga un -{{discount}}% en su pedido al inscribirse para recibir nuestros emails',
    signUpPopDisclaimer: 'Sí, deseo recibir emails con ofertas especiales de VistaPrint, así como noticias sobre productos, servicios y mis diseños que están en proceso. Lea nuestra {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: 'Política de privacidad y cookies',
    signUpPopInputPlaceholder: 'Email para la suscripción',
    signUpPopInvalidEmailMessage: 'La dirección de email no es válida. Ejemplo de email: yo@email.com',
    signUpPopCallToActionButton: 'Enviar',
    signUpPopCancelButton: 'No gracias',
    signUpPopLoadingCoupon: 'Cargando',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Ser parte de la lista se siente bien.',
    signUpPopSubcopyMobile: 'Obtenga un -{{discount}}% en su pedido al inscribirse para recibir nuestros emails',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: '¡Gracias, ya está en la lista!',
    signUpConfirmationSubcopy: '(Y su código promocional va en camino).',
    signUpConfirmationBodyCopy: 'Revise su correo electrónico, recibirá el código promocional en su bandeja de entrada.*',
    signUpConfirmationDisclaimer: '*La oferta vence {{expirationDate}}. Solo se puede usar un código por pedido al finalizar la compra. A menos que se indique lo contrario, este código no tiene validez combinado con otras ofertas o en entregas, en opciones de productos, servicios de diseño, productos digitales o en el sitio de Promotique. Nos reservamos el derecho a cambiar, cancelar y limitar todas las ofertas.',
    signUpConfirmationOkButton: 'Aceptar',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: '¡Vaya! Se produjo un error.',
    signUpErrorBodyCopy: 'No pudimos registrarle para nuestros emails. Vuelva a intentarlo más tarde o comuníquese con el equipo de atención al cliente para recibir asistencia.'
};

var fiFI = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'On hyvä pysyä ajan tasalla.',
    signUpPopSubcopy: 'Saat {{discount}} % alennusta tilauksestasi, kun liityt sähköpostilistallemme',
    signUpPopDisclaimer: 'Kyllä, haluan kuulla VistaPrintin erikoistarjouksista sähköpostitse ja saada tietoa tuotteista, palveluista ja meneillään olevista projekteistani. Lue {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: 'Tietosuoja- ja evästekäytäntö',
    signUpPopInputPlaceholder: 'Tilaussähköposti',
    signUpPopInvalidEmailMessage: 'Virheellinen sähköpostiosoite. Esimerkkiosoite: nimi@sähköposti.com',
    signUpPopCallToActionButton: 'Lähetä',
    signUpPopCancelButton: 'Ei kiitos',
    signUpPopLoadingCoupon: 'Ladataan',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'On hyvä pysyä ajan tasalla.',
    signUpPopSubcopyMobile: 'Saat {{discount}} % alennusta tilauksestasi, kun liityt sähköpostilistallemme',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Kiitos – olet nyt listalla!',
    signUpConfirmationSubcopy: '(Ja alennuskoodisi on matkalla luoksesi.)',
    signUpConfirmationBodyCopy: 'Tarkista sähköpostisi – saat alennuskoodin Saapuneet-kansioosi.*',
    signUpConfirmationDisclaimer: '*Tarjous vanhenee {{expirationDate}}. Voit käyttää vain yhden koodin tilausta kohti kassalla. Mikäli muutoin ei ole ilmoitettu, tätä koodia ei voida yhdistää muihin tarjouksiin eikä se koske toimitusta, tuotevaihtoehtoja, suunnittelupalveluita eikä Promotique-sivuston tuotteita. Pidätämme oikeuden muuttaa, peruuttaa ja rajoittaa mitä tahansa tarjousta.',
    signUpConfirmationOkButton: 'Ok',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oho! Jokin meni vikaan.',
    signUpErrorBodyCopy: 'Emme onnistuneet rekisteröimään sinua sähköpostilistallemme. Yritä myöhemmin uudelleen tai pyydä apua ottamalla yhteys asiakaspalveluumme.'
};

var frBE = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Bienvenue sur notre liste de diffusion.',
    signUpPopSubcopy: 'Profitez de -{{discount}}% sur votre commande en vous inscrivant à notre newsletter',
    signUpPopDisclaimer: 'Oui, je souhaite recevoir des offres spéciales par e-mail de la part de VistaPrint, ainsi que des informations sur les produits, les services et mes créations en cours. Lisez notre {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: "Politique de confidentialité et d'utilisation des cookies",
    signUpPopInputPlaceholder: 'Votre adresse e-mail',
    signUpPopInvalidEmailMessage: 'Adresse e-mail non valide. Exemple d’adresse e-mail : moi@email.com',
    signUpPopCallToActionButton: 'Envoyer',
    signUpPopCancelButton: 'Non merci',
    signUpPopLoadingCoupon: 'Chargement',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Bienvenue sur notre liste de diffusion.',
    signUpPopSubcopyMobile: 'Profitez de -{{discount}}% sur votre commande en vous inscrivant à notre newsletter',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Merci ! Vous avez bien été ajouté à la liste de diffusion.',
    signUpConfirmationSubcopy: 'Votre code promo va bientôt arriver.',
    signUpConfirmationBodyCopy: 'Vérifiez votre boîte de réception : vous allez recevoir un e-mail contenant votre code promo.*',
    signUpConfirmationDisclaimer: "* L'offre prend fin le {{expirationDate}}. Vous ne pouvez utiliser qu'un seul code par commande lors du paiement. Sauf indication contraire, ce code n'est pas cumulable avec d'autres offres et ne s'applique pas à la livraison, aux options de produits, aux services graphiques, aux produits numériques ou au site Promotique. Nous nous réservons le droit de modifier, d'annuler ou de limiter toutes les offres.",
    signUpConfirmationOkButton: 'OK',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oups ! Une erreur est survenue.',
    signUpErrorBodyCopy: 'L’inscription à nos e-mails a échoué. Veuillez réessayer plus tard ou contacter notre service client.'
};

var frCA = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Il y a des avantages à être sur la liste!',
    signUpPopSubcopy: 'Bénéficiez d’une réduction de {{discount}}% sur votre commande en vous abonnant à nos courriels',
    signUpPopDisclaimer: 'Oui, je souhaite recevoir des offres spéciales de la part de VistaPrint, ainsi que l’actualité sur les produits, les services et mes créations en cours. Lisez notre {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: 'politique de confidentialité et d’utilisation de témoins',
    signUpPopInputPlaceholder: 'Courriel d’abonnement',
    signUpPopInvalidEmailMessage: 'Adresse courriel non valide. Exemple : moi@moncourriel.com',
    signUpPopCallToActionButton: 'Envoyer',
    signUpPopCancelButton: 'Non, merci',
    signUpPopLoadingCoupon: 'Chargement en cours',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Il y a des avantages à être sur la liste!',
    signUpPopSubcopyMobile: 'Bénéficiez d’une réduction de {{discount}}% sur votre commande en vous abonnant à nos courriels',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Merci, vous faites maintenant partie de nos abonnés!',
    signUpConfirmationSubcopy: '(Et votre code promo vous parviendra très bientôt.)',
    signUpConfirmationBodyCopy: 'Restez à l’affût de vos courriels, vous recevrez prochainement votre code promo.*',
    signUpConfirmationDisclaimer: '* Offre valable jusqu’au {{expirationDate}}. Vous ne pouvez utiliser qu’un seul code par commande, à saisir au moment du paiement. Sauf indication contraire, ce code n’est pas cumulable avec d’autres offres. Il ne s’applique pas aux frais de livraison, aux options de produits, aux services graphiques, aux produits numériques ou au site Promotique. Nous nous réservons le droit de modifier, annuler ou limiter toute offre.',
    signUpConfirmationOkButton: 'OK',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oups! Une erreur s’est produite.',
    signUpErrorBodyCopy: 'Nous n’avons pas pu vous abonner à nos courriels. Veuillez réessayer plus tard ou contacter notre service à la clientèle pour obtenir de l’aide.'
};

var frCH = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Bienvenue sur notre liste de diffusion.',
    signUpPopSubcopy: 'Profitez de -{{discount}}% sur votre commande en vous inscrivant à notre newsletter',
    signUpPopDisclaimer: 'Oui, je souhaite recevoir des offres spéciales par e-mail de la part de VistaPrint, ainsi que des informations sur les produits, les services et mes créations en cours. Lisez notre {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: "Politique de confidentialité et d'utilisation des cookies",
    signUpPopInputPlaceholder: 'Votre adresse e-mail',
    signUpPopInvalidEmailMessage: 'Adresse e-mail non valide. Exemple d’adresse e-mail : moi@email.com',
    signUpPopCallToActionButton: 'Envoyer',
    signUpPopCancelButton: 'Non merci',
    signUpPopLoadingCoupon: 'Chargement',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Bienvenue sur notre liste de diffusion.',
    signUpPopSubcopyMobile: 'Profitez de -{{discount}}% sur votre commande en vous inscrivant à notre newsletter',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Merci ! Vous avez bien été ajouté à la liste de diffusion.',
    signUpConfirmationSubcopy: 'Votre code promo va bientôt arriver.',
    signUpConfirmationBodyCopy: 'Vérifiez votre boîte de réception : vous allez recevoir un e-mail contenant votre code promo.*',
    signUpConfirmationDisclaimer: "* L'offre prend fin le {{expirationDate}}. Vous ne pouvez utiliser qu'un seul code par commande lors du paiement. Sauf indication contraire, ce code n'est pas cumulable avec d'autres offres et ne s'applique pas à la livraison, aux options de produits, aux services graphiques, aux produits numériques ou au site Promotique. Nous nous réservons le droit de modifier, d'annuler ou de limiter toutes les offres.",
    signUpConfirmationOkButton: 'OK',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oups ! Une erreur est survenue.',
    signUpErrorBodyCopy: 'L’inscription à nos e-mails a échoué. Veuillez réessayer plus tard ou contacter notre service client.'
};

var frFR = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Bienvenue sur notre liste de diffusion.',
    signUpPopSubcopy: 'Profitez de -{{discount}}% sur votre commande en vous inscrivant à notre newsletter',
    signUpPopDisclaimer: 'Oui, je souhaite recevoir des offres spéciales par e-mail de la part de VistaPrint, ainsi que des informations sur les produits, les services et mes créations en cours. Lisez notre {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: "Politique de confidentialité et d'utilisation des cookies",
    signUpPopInputPlaceholder: 'Votre adresse e-mail',
    signUpPopInvalidEmailMessage: 'Adresse e-mail non valide. Exemple d’adresse e-mail : moi@email.com',
    signUpPopCallToActionButton: 'Envoyer',
    signUpPopCancelButton: 'Non merci',
    signUpPopLoadingCoupon: 'Chargement',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Bienvenue sur notre liste de diffusion.',
    signUpPopSubcopyMobile: 'Profitez de -{{discount}}% sur votre commande en vous inscrivant à notre newsletter',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Merci ! Vous avez bien été ajouté à la liste de diffusion.',
    signUpConfirmationSubcopy: 'Votre code promo va bientôt arriver.',
    signUpConfirmationBodyCopy: 'Vérifiez votre boîte de réception : vous allez recevoir un e-mail contenant votre code promo.*',
    signUpConfirmationDisclaimer: "* L'offre prend fin le {{expirationDate}}. Vous ne pouvez utiliser qu'un seul code par commande lors du paiement. Sauf indication contraire, ce code n'est pas cumulable avec d'autres offres et ne s'applique pas à la livraison, aux options de produits, aux services graphiques, aux produits numériques ou au site Promotique. Nous nous réservons le droit de modifier, d'annuler ou de limiter toutes les offres.",
    signUpConfirmationOkButton: 'OK',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oups ! Une erreur est survenue.',
    signUpErrorBodyCopy: 'L’inscription à nos e-mails a échoué. Veuillez réessayer plus tard ou contacter notre service client.'
};

var itCH = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Conviene essere in lista.',
    signUpPopSubcopy: '-{{discount}}% sul tuo ordine quando ti registri per le nostre e-mail',
    signUpPopDisclaimer: 'Sì, desidero ricevere e-mail contenenti offerte speciali da VistaPrint, nonché novità sui prodotti, i servizi e i miei design in corso d’opera. Leggi la nostra {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: "Informativa sulla privacy e sull'utilizzo dei cookie",
    signUpPopInputPlaceholder: 'Email di abbonamento',
    signUpPopInvalidEmailMessage: 'Indirizzo email non valido. Esempio email: me@email.com',
    signUpPopCallToActionButton: 'Invia',
    signUpPopCancelButton: 'No, grazie',
    signUpPopLoadingCoupon: 'Caricamento in corso',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Conviene essere in lista.',
    signUpPopSubcopyMobile: '-{{discount}}% sul tuo ordine quando ti registri per le nostre e-mail',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Grazie. Il tuo indirizzo è stato aggiunto all’elenco.',
    signUpConfirmationSubcopy: 'E a breve riceverai il tuo codice sconto.',
    signUpConfirmationBodyCopy: 'Controlla la tua e-mail: riceverai un messaggio con il tuo codice sconto.*',
    signUpConfirmationDisclaimer: '*L’offerta scade il giorno {{expirationDate}}. È possibile utilizzare un solo codice per ordine alla conferma. Salvo ove diversamente specificato, questo codice non è valido se combinato con altre offerte e non può essere utilizzato su spedizione, opzioni prodotto, servizi grafici, prodotti digitali o sul sito Promotique. Ci riserviamo il diritto di modificare, annullare e limitare qualsiasi offerta.',
    signUpConfirmationOkButton: 'OK',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Ops, si è verificato un errore.',
    signUpErrorBodyCopy: 'Non è stato possibile iscriverti alle nostre e-mail. Riprova più tardi oppure contatta il nostro servizio di assistenza clienti.'
};

var itIT = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Conviene essere in lista.',
    signUpPopSubcopy: '-{{discount}}% sul tuo ordine quando ti registri per le nostre e-mail',
    signUpPopDisclaimer: 'Sì, desidero ricevere e-mail contenenti offerte speciali da VistaPrint, nonché novità sui prodotti, i servizi e i miei design in corso d’opera. Leggi la nostra {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: "Informativa sulla privacy e sull'utilizzo dei cookie",
    signUpPopInputPlaceholder: 'Email di abbonamento',
    signUpPopInvalidEmailMessage: 'Indirizzo email non valido. Esempio email: me@email.com',
    signUpPopCallToActionButton: 'Invia',
    signUpPopCancelButton: 'No, grazie',
    signUpPopLoadingCoupon: 'Caricamento in corso',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Conviene essere in lista.',
    signUpPopSubcopyMobile: '-{{discount}}% sul tuo ordine quando ti registri per le nostre e-mail',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Grazie. Il tuo indirizzo è stato aggiunto all’elenco.',
    signUpConfirmationSubcopy: 'E a breve riceverai il tuo codice sconto.',
    signUpConfirmationBodyCopy: 'Controlla la tua e-mail: riceverai un messaggio con il tuo codice sconto.*',
    signUpConfirmationDisclaimer: '*L’offerta scade il giorno {{expirationDate}}. È possibile utilizzare un solo codice per ordine alla conferma. Salvo ove diversamente specificato, questo codice non è valido se combinato con altre offerte e non può essere utilizzato su spedizione, opzioni prodotto, servizi grafici, prodotti digitali o sul sito Promotique. Ci riserviamo il diritto di modificare, annullare e limitare qualsiasi offerta.',
    signUpConfirmationOkButton: 'OK',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Ops, si è verificato un errore.',
    signUpErrorBodyCopy: 'Non è stato possibile iscriverti alle nostre e-mail. Riprova più tardi oppure contatta il nostro servizio di assistenza clienti.'
};

var nbNO = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Det er lurt å melde seg på.',
    signUpPopSubcopy: 'Få {{discount}} % rabatt på den neste bestillingen din hvis du melder deg på e-postene våre',
    signUpPopDisclaimer: 'Ja, jeg vil gjerne motta spesialtilbud fra VistaPrint samt nyheter om produkter, tjenester og designene mine i produksjon. Les våre {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: 'Regler for personvern og informasjonskapsler',
    signUpPopInputPlaceholder: 'Påmelding e-post',
    signUpPopInvalidEmailMessage: 'Ugyldig e-postadresse. Eksempel e-post: meg@e-post.com',
    signUpPopCallToActionButton: 'Send',
    signUpPopCancelButton: 'Nei takk',
    signUpPopLoadingCoupon: 'Laster inn',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Det er lurt å melde seg på.',
    signUpPopSubcopyMobile: 'Få {{discount}} % rabatt på den neste bestillingen din hvis du melder deg på e-postene våre',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Takk – du er på listen!',
    signUpConfirmationSubcopy: '(Og kampanjekoden din er på vei.)',
    signUpConfirmationBodyCopy: 'Sjekk e-posten din – du vil motta kampanjekoden i innboksen din.*',
    signUpConfirmationDisclaimer: '*Tilbudet utløper {{expirationDate}}. Du kan kun bruke én kode per bestilling i kassen. Med mindre annet er oppgitt, er denne koden ikke gyldig i kombinasjon med andre tilbud, levering, produktalternativer, designtjenester, digitale produkter eller Promotique-siden. Vi forbeholder oss retten til å endre, kansellere og begrense tilbud.',
    signUpConfirmationOkButton: 'OK',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oisann! Noe gikk galt.',
    signUpErrorBodyCopy: 'Vi klarte ikke å registrere deg for e-postene våre. Prøv igjen senere eller ta kontakt med kundeservice.'
};

var nlBE = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Het is voordelig om je aan te melden.',
    signUpPopSubcopy: 'Ontvang {{discount}}% korting op je bestelling als je je aanmeldt voor onze e-mails',
    signUpPopDisclaimer: 'Ja, ik ontvang graag e-mails met speciale aanbiedingen van VistaPrint, evenals nieuws over producten, diensten en mijn ontwerpen in uitvoering. Lees ons {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: 'Privacy- en cookiebeleid',
    signUpPopInputPlaceholder: 'Inschrijvingse-mail',
    signUpPopInvalidEmailMessage: 'Ongeldig e-mailadres. Voorbeeld e-mailadres: ik@email.com',
    signUpPopCallToActionButton: 'Verzenden',
    signUpPopCancelButton: 'Nee, geen interesse',
    signUpPopLoadingCoupon: 'Bezig met laden',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Het is voordelig om je aan te melden.',
    signUpPopSubcopyMobile: 'Ontvang {{discount}}% korting op je bestelling als je je aanmeldt voor onze e-mails',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Bedankt voor je aanmelding!',
    signUpConfirmationSubcopy: '(En je promocode is onderweg.)',
    signUpConfirmationBodyCopy: 'Controleer je e-mail, want je ontvangt de promocode in je inbox.*',
    signUpConfirmationDisclaimer: '*Aanbieding vervalt {{expirationDate}}. Je kunt per bestelling maar één code gebruiken op de betaalpagina. Tenzij anders vermeld is deze code niet geldig in combinatie met andere aanbiedingen of op bezorging, ontwerpdiensten, digitale producten of de Promotique-site. We behouden ons het recht voor om elke aanbieding te wijzigen, te annuleren of te beperken.',
    signUpConfirmationOkButton: 'OK',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oeps! Er is iets fout gegaan.',
    signUpErrorBodyCopy: 'We konden je niet aanmelden voor onze e-mails. Probeer het later opnieuw of neem voor hulp contact op met onze klantenservice.'
};

var nlNL = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Het is voordelig om je aan te melden.',
    signUpPopSubcopy: 'Ontvang {{discount}}% korting op je bestelling als je je aanmeldt voor onze e-mails',
    signUpPopDisclaimer: 'Ja, ik ontvang graag e-mails met speciale aanbiedingen van VistaPrint, evenals nieuws over producten, diensten en mijn ontwerpen in uitvoering. Lees ons {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: 'Privacy- en cookiebeleid',
    signUpPopInputPlaceholder: 'Inschrijvingse-mail',
    signUpPopInvalidEmailMessage: 'Ongeldig e-mailadres. Voorbeeld e-mailadres: ik@email.com',
    signUpPopCallToActionButton: 'Verzenden',
    signUpPopCancelButton: 'Nee, geen interesse',
    signUpPopLoadingCoupon: 'Bezig met laden',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Het is voordelig om je aan te melden.',
    signUpPopSubcopyMobile: 'Ontvang {{discount}}% korting op je bestelling als je je aanmeldt voor onze e-mails',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Bedankt voor je aanmelding!',
    signUpConfirmationSubcopy: '(En je promocode is onderweg.)',
    signUpConfirmationBodyCopy: 'Controleer je e-mail, want je ontvangt de promocode in je inbox.*',
    signUpConfirmationDisclaimer: '*Aanbieding vervalt {{expirationDate}}. Je kunt per bestelling maar één code gebruiken op de betaalpagina. Tenzij anders vermeld is deze code niet geldig in combinatie met andere aanbiedingen of op bezorging, ontwerpdiensten, digitale producten of de Promotique-site. We behouden ons het recht voor om elke aanbieding te wijzigen, te annuleren of te beperken.',
    signUpConfirmationOkButton: 'OK',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Oeps! Er is iets fout gegaan.',
    signUpErrorBodyCopy: 'We konden je niet aanmelden voor onze e-mails. Probeer het later opnieuw of neem voor hulp contact op met onze klantenservice.'
};

var ptPT = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'É bom estar na lista.',
    signUpPopSubcopy: 'Obtenha {{discount}}% de desconto na sua encomenda ao subscrever os nossos e-mails',
    signUpPopDisclaimer: 'Sim, gostaria de receber ofertas especiais por e-mail da VistaPrint, bem como novidades sobre produtos, serviços e os meus designs em curso. Leia as nossas {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: 'Política de privacidade e de cookies',
    signUpPopInputPlaceholder: 'E-mail da subscrição',
    signUpPopInvalidEmailMessage: 'Endereço de e-mail inválido. Exemplo de e-mail: eu@email.com',
    signUpPopCallToActionButton: 'Enviar',
    signUpPopCancelButton: 'Não, obrigado',
    signUpPopLoadingCoupon: 'A carregar',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'É bom estar na lista.',
    signUpPopSubcopyMobile: 'Obtenha {{discount}}% de desconto na sua encomenda ao subscrever os nossos e-mails',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Obrigado, foi adicionado(a) à nossa lista.',
    signUpConfirmationSubcopy: '(E o seu código promocional está a caminho.)',
    signUpConfirmationBodyCopy: 'Verifique o seu e-mail. Receberá o código promocional na sua caixa de entrada.*',
    signUpConfirmationDisclaimer: '*A oferta expira em {{expirationDate}}. Só pode utilizar um código por encomenda na finalização da compra. Salvo expresso em contrário, este código não é válido em conjunto com outras ofertas nem na entrega, em opções de produtos, serviços de design, produtos digitais ou no site Promotique. Reservamo-nos o direito de alterar, cancelar ou limitar qualquer oferta.',
    signUpConfirmationOkButton: 'Ok',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Ups! Ocorreu um erro.',
    signUpErrorBodyCopy: 'Não foi possível inscrevê-lo nos nossos e-mails. Tente novamente mais tarde ou contacte a nossa equipa do serviço de apoio ao cliente para obter ajuda.'
};

var svSE = {
    __commentRequest: 'Sign up pop texts',
    signUpPopHeadline: 'Det är bra att stå på listan',
    signUpPopSubcopy: 'Få {{discount}} % rabatt på din beställning när du registrerar dig för att få våra mejl.',
    signUpPopDisclaimer: 'Ja, jag vill få specialerbjudanden per mejl från VistaPrint och nyheter om produkter, tjänster och mina pågående designprojekt. Läs vår {{signUpPopDisclaimerPrivacyLink}}.',
    signUpPopDisclaimerPrivacyLink: 'Sekretess- och cookiepolicy',
    signUpPopInputPlaceholder: 'E-postadress för prenumeration',
    signUpPopInvalidEmailMessage: 'Ogiltig e-postadress. Exempel på e-post: jag@mejl.com',
    signUpPopCallToActionButton: 'Skicka',
    signUpPopCancelButton: 'Nej tack',
    signUpPopLoadingCoupon: 'Laddar',
    __commentRequestMobile: 'Sign up pop texts for mobile',
    signUpPopHeadlineMobile: 'Det är bra att stå på listan',
    signUpPopSubcopyMobile: 'Få {{discount}} % rabatt på din beställning när du registrerar dig för att få våra mejl.',
    __commentConfirmation: 'Sign up confirmation texts',
    signUpConfirmationHeadline: 'Tack – nu står du med på listan!',
    signUpConfirmationSubcopy: '(Och din rabattkod är på väg.)',
    signUpConfirmationBodyCopy: 'Kolla din e-post – du får rabattkoden i din inkorg.*',
    signUpConfirmationDisclaimer: '* Erbjudandet löper ut {{expirationDate}}. Du kan bara använda en kod per beställning i kassan. Om inget annat anges är koden inte giltig i kombination med andra erbjudanden eller på leveranskostnaden, produktalternativ, designtjänster, digitala produkter eller webbplatsen Promotique. Vi förbehåller oss rätten att ändra, avbryta och begränsa alla erbjudanden.',
    signUpConfirmationOkButton: 'OK',
    __commentSignupError: 'Sign up error texts',
    signUpErrorHeadline: 'Hoppsan! Något gick snett.',
    signUpErrorBodyCopy: 'Vi kunde inte lägga till dig till våra e-postutskick. Försök igen senare eller kontakta kundservice för att få hjälp.'
};

var Localizer = /** @class */ (function () {
    function Localizer(language) {
        this.strings = new b({
            en: defaultLocale,
            'da-dk': daDK,
            'de-at': deAT,
            'de-ch': deCH,
            'de-de': deDE,
            'en-au': enAU,
            'en-ca': enCA,
            'en-gb': enGB,
            'en-ie': enIE,
            'en-in': enIN,
            'en-nz': enNZ,
            'en-sg': enSG,
            'en-us': enUS,
            'es-es': esES,
            'es-us': esUS,
            'fi-fi': fiFI,
            'fr-be': frBE,
            'fr-ca': frCA,
            'fr-ch': frCH,
            'fr-fr': frFR,
            'it-ch': itCH,
            'it-it': itIT,
            'nl-be': nlBE,
            'nl-nl': nlNL,
            'nb-no': nbNO,
            'pt-pt': ptPT,
            'sv-se': svSE
        });
        this.strings.setLanguage(language.toLowerCase()); // upper-case country codes don't work properly
    }
    Localizer.prototype.getString = function (key) {
        return this.strings.getString(key) || '';
    };
    return Localizer;
}());

var dateToLocalizedString = function (date, language) {
    if (date === undefined) {
        return undefined;
    }
    var options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    };
    return date.toLocaleDateString(language, options);
};
var isoDateStringToDate = function (isoDate) {
    if (isoDate === undefined) {
        return undefined;
    }
    var _a = isoDate.split('-'), yearString = _a[0], monthString = _a[1], dayString = _a[2];
    var year = parseInt(yearString);
    var month = parseInt(monthString) - 1; // because in JS months are from 0 to 11
    var day = parseInt(dayString);
    return new Date(Date.UTC(year, month, day));
};

/**
 * From https://ui.dev/validate-email-address-javascript/
 */
var emailIsValid = function (email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

var EventTracker = /** @class */ (function () {
    function EventTracker(tracking, placementData) {
        var _this = this;
        this.getTrackingEventProperties = function () {
            return __assign({ route: EventTracker.SEGMENT_TRACKING_CUSTOM_ROUTE, category: EventTracker.PROMOTIONAL_OFFER_EVENT_CATEGORY, label: EventTracker.PROMOTIONAL_OFFER_PLACEMENT_LABEL }, _this.placementData);
        };
        this.sendActionEvent = function (actionLabel) {
            if (_this.tracking === undefined) {
                return;
            }
            _this.tracking.track(actionLabel, _this.getTrackingEventProperties(), {});
        };
        this.offerPlacementShown = function () {
            _this.sendActionEvent(EventTracker.PROMOTIONAL_OFFER_SHOWN_ACTION);
        };
        this.offerPlacementAccepted = function () {
            _this.sendActionEvent(EventTracker.PROMOTIONAL_OFFER_ACCEPTED_ACTION);
        };
        this.tracking = tracking;
        this.placementData = placementData;
    }
    EventTracker.SEGMENT_TRACKING_CUSTOM_ROUTE = 'channel';
    EventTracker.PROMOTIONAL_OFFER_EVENT_CATEGORY = 'Opt placement';
    EventTracker.PROMOTIONAL_OFFER_PLACEMENT_LABEL = 'Component';
    EventTracker.PROMOTIONAL_OFFER_SHOWN_ACTION = 'Opt Placement Viewed';
    EventTracker.PROMOTIONAL_OFFER_ACCEPTED_ACTION = 'Opt Placement Submit';
    return EventTracker;
}());

var createAuthorizationHeaderValue = function (authorization) {
    return "".concat(authorization.type, " ").concat(authorization.token);
};
var createAuthorization = function (auth) {
    if (typeof auth === 'string') {
        var _a = auth.split(' '), first = _a[0], second = _a[1];
        if (second === undefined) {
            // this is just the plain token, assume it's a bearer token
            return returnValidatedOrUndefined('Bearer', first);
        }
        return returnValidatedOrUndefined(first, second);
    }
    return returnValidatedOrUndefined(auth.type, auth.token);
};
var returnValidatedOrUndefined = function (type, token) {
    if (type === 'Bearer' || type === 'Session') {
        if (token !== undefined && token !== '') {
            return {
                type: type,
                token: token
            };
        }
    }
    return undefined;
};

var typedFetch = function (input, init) {
    return fetch(input, init).then(function (response) { return response.json(); });
};

var PecsEnvironment;
(function (PecsEnvironment) {
    PecsEnvironment["Production"] = "https://pecs.ct.vpsvc.com";
    PecsEnvironment["Staging"] = "https://pecs.dev.ct.vpsvc.com";
})(PecsEnvironment || (PecsEnvironment = {}));
var shouldComponentBeShown = function (placementId, tenant, country, language, authorization, environment) {
    var baseURL = environment !== null && environment !== void 0 ? environment : PecsEnvironment.Production;
    var headers = { Authorization: createAuthorizationHeaderValue(authorization) };
    return typedFetch(baseURL + "/api/Placements/".concat(placementId, "/show?tenant=").concat(tenant, "&country=").concat(country, "&lang=").concat(language), {
        headers: headers
    });
};

var UseShowComponentStateActionType;
(function (UseShowComponentStateActionType) {
    UseShowComponentStateActionType[UseShowComponentStateActionType["SetComponentToBeShown"] = 0] = "SetComponentToBeShown";
    UseShowComponentStateActionType[UseShowComponentStateActionType["SetComponentNotToBeShown"] = 1] = "SetComponentNotToBeShown";
    UseShowComponentStateActionType[UseShowComponentStateActionType["SetComponentForError"] = 2] = "SetComponentForError";
})(UseShowComponentStateActionType || (UseShowComponentStateActionType = {}));
var createReducer = function (placementInfoMapper) {
    return function (_, action) {
        switch (action.type) {
            case UseShowComponentStateActionType.SetComponentToBeShown:
                return {
                    showComponent: true,
                    email: action.payload.email,
                    isAnonymous: action.payload.isAnonymous,
                    placementInfo: placementInfoMapper(action.payload.placementInfo)
                };
            case UseShowComponentStateActionType.SetComponentNotToBeShown:
                return {
                    showComponent: false,
                    email: action.payload.email,
                    isAnonymous: action.payload.isAnonymous
                };
            case UseShowComponentStateActionType.SetComponentForError:
                return {
                    showComponent: false,
                    errorState: action.payload
                };
        }
    };
};
var initialState = {
    showComponent: false,
    isAnonymous: true
};
/**
 * This is a React hook - this is why this file needs to be a .tsx, even though we're not even importing React
 */
var useShowComponent = function (placementInfoMapper, placementId, tenant, country, language, authorization, logger, environment) {
    // Use typed reducer
    var _a = useReducer(createReducer(placementInfoMapper), initialState), state = _a[0], dispatch = _a[1];
    useEffect(function () {
        shouldComponentBeShown(placementId, tenant, country, language, authorization, environment)
            .then(function (response) {
            if (response.show) {
                dispatch({
                    type: UseShowComponentStateActionType.SetComponentToBeShown,
                    payload: {
                        email: undefined, // Explicitly set undefined for clarity
                        isAnonymous: response.isAnonymous,
                        placementInfo: response.placementInfo
                    }
                });
            }
            else {
                dispatch({
                    type: UseShowComponentStateActionType.SetComponentNotToBeShown,
                    payload: {
                        email: undefined, // Explicitly set undefined for clarity
                        isAnonymous: response.isAnonymous
                    }
                });
            }
        }).catch(function (error) {
            logger.warn('PECS request failed', error);
            dispatch({
                type: UseShowComponentStateActionType.SetComponentForError,
                payload: error
            });
        });
    }, [placementId, tenant, country, authorization, environment]);
    return state;
};

var CucosEnvironment;
(function (CucosEnvironment) {
    CucosEnvironment["Production"] = "https://cucos.ct.vpsvc.com/api/v2";
    CucosEnvironment["Staging"] = "https://cucos.dev.ct.vpsvc.com/api/v2";
})(CucosEnvironment || (CucosEnvironment = {}));
var client = 'email_subscription_component';
var channel = 'promo_email';
var registeredCustomerEndpoint = '/subscriptions/mutation';
var anonymousCustomerEndpoint = '/subscriptions/mutation/anonymous';
var updateSubscriptionPreference = function (email, tenant, authorization, preference, isAnonymous, options) {
    var _a, _b;
    var mockApi = (_a = options === null || options === void 0 ? void 0 : options.mockApis) !== null && _a !== void 0 ? _a : false;
    var environment = (_b = options === null || options === void 0 ? void 0 : options.environment) !== null && _b !== void 0 ? _b : CucosEnvironment.Production;
    if (mockApi) {
        return Promise.resolve();
    }
    var userData = {
        tenant: tenant,
        channel: channel,
        contact: email,
        preference: preference,
        client: client
    };
    var endpoint = isAnonymous
        ? anonymousCustomerEndpoint
        : registeredCustomerEndpoint;
    return fetch(environment + endpoint, {
        method: 'POST',
        body: JSON.stringify(userData),
        headers: {
            Authorization: createAuthorizationHeaderValue(authorization),
            'Content-Type': 'application/json'
        }
    }).then(function (response) {
        if (!response.ok) {
            var errorPayload = response.json().catch(function () { return ({}); });
            throw new Error("Subscription failed: ".concat(response.status), { cause: errorPayload });
        }
    });
};
var subscribeUser = function (email, tenant, authorization, isAnonymous, options) {
    return updateSubscriptionPreference(email, tenant, authorization, 'opt-in', isAnonymous, options);
};

// Images for AU and NZ are the same ones as for IE: https://vistaprint.atlassian.net/browse/CT-3028
// Complete list of images: https://vistaprint.atlassian.net/wiki/spaces/CCAD/pages/4738221756/Email+Component+-+What+is+live+right+now
var localizedImages = {
    'da-dk': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/da-dk/S001940420.png',
    'de-at': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/de-at/S001940420.png',
    'de-ch': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/de-ch/S001940420.png',
    'de-de': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/de-de/S001940420.png',
    'en-au': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/v1711678138/legacy_dam/en-us/S001744016.png',
    'en-ca': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/v1711678138/legacy_dam/en-ca/S001744016.png',
    'en-gb': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/en-gb/S001940420.png',
    'en-ie': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/en-ie/S001940420.png',
    'en-in': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/v1738935047/India%20LOB/Email%20Signup/Email_Signup_Banner_Visiting_card.png',
    'en-nz': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/v1711678138/legacy_dam/en-us/S001744016.png',
    'en-sg': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/v1706609319/site-merchandising/422a1ad0-111d-4591-b6a5-5707606e6307/en-au.png',
    'en-us': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/v1711678138/legacy_dam/en-us/S001744016.png',
    'es-es': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/es-es/S001940420.png',
    'es-us': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/v1711678135/legacy_dam/es-us/S001744016.png',
    'fi-fi': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/fi-fi/S001940420.png',
    'fr-be': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/fr-be/S001940420.png',
    'fr-ca': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/v1711678137/legacy_dam/fr-ca/S001744016.png',
    'fr-ch': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/fr-ch/S001940420.png',
    'fr-fr': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/fr-fr/S001940420.png',
    'it-ch': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/it-ch/S001940420.png',
    'it-it': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/it-it/S001940420.png',
    'nb-no': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/nb-no/S001940420.png',
    'nl-be': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/nl-be/S001940420.png',
    'nl-nl': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/nl-nl/S001940420.png',
    'pt-pt': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/pt-pt/S001940420.png',
    'sv-se': 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/legacy_dam/sv-se/S001940420.png',
};
function getImage(key, cloudinaryWidth) {
    var _a;
    var lowerCaseKey = key.toLocaleLowerCase();
    // by default let's return the previous version of "en-ie"
    var fallbackImage = 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto,{{cloudinary-width}}/v1637316299/desktop-en-IE.png';
    var localizedImage = (_a = localizedImages[lowerCaseKey]) !== null && _a !== void 0 ? _a : fallbackImage;
    return localizedImage.replace('{{cloudinary-width}}', cloudinaryWidth);
}

/* eslint-disable @typescript-eslint/no-explicit-any */
var createTypelessRef = function () {
    return createRef();
};

var SignUpOffer = function (props) {
    var _a = useState(''), customerEmail = _a[0], setCustomerEmail = _a[1];
    var _b = useState(false), focused = _b[0], setFocused = _b[1];
    var onFocus = function () { return setFocused(true); };
    var onBlur = function () { return setFocused(false); };
    var onEmailChange = function (event) {
        setCustomerEmail(event.target.value);
    };
    var discountString = "".concat(props.discount);
    var emailInputPlaceholderText = props.strings.getString('signUpPopInputPlaceholder');
    var invalidEmailErrorText = props.strings.getString('signUpPopInvalidEmailMessage');
    var invalidEmail = customerEmail !== '' && !emailIsValid(customerEmail);
    var showInvalidEmailError = !focused && invalidEmail;
    return (jsx(Box, { children: jsxs(StandardForm, { children: [jsx(Typography, { textAlign: "center", fontSize: "x2large", marginBottom: 5, fontWeight: "bold", children: props.strings.getString('signUpPopHeadline').replace('{{discount}}', discountString) }), jsx(Typography, { textAlign: "center", fontWeight: "bold", fontSize: "large", marginBottom: 7, children: props.strings.getString('signUpPopSubcopy').replace('{{discount}}', discountString) }), jsxs(FormField, { children: [jsxs(InputWithFloatingLabel, { children: [jsx(TextInput, { value: customerEmail, type: "email", onChange: onEmailChange, onFocus: onFocus, onBlur: onBlur, "aria-required": true, "aria-invalid": invalidEmail, "aria-describedby": "email-input-error", marginBottom: 2, enterKeyHint: undefined, autoComplete: "email" }), jsx(FloatingLabel, { children: emailInputPlaceholderText })] }), jsx("div", { role: "status", "aria-live": "polite", children: (showInvalidEmailError && jsx(FormError, { id: "email-input-error", children: invalidEmailErrorText })) })] }), jsx(LegalCopy, { privacyPolicyUrl: props.privacyPolicyUrl, strings: props.strings }), jsxs(GridContainer, { style: { width: '100%', marginTop: 30 }, children: [(props.signingUp && jsx(Spinner, { accessibleText: props.strings.getString('signUpPopLoadingCoupon') })), jsx(Row, { style: { justifyContent: 'center' }, children: jsx(SignMeUpButton, __assign({}, props, { customerEmail: customerEmail })) })] })] }) }));
};
var LegalCopy = function (props) {
    var _a = props.strings.getString('signUpPopDisclaimer').split('{{signUpPopDisclaimerPrivacyLink}}'), legalCopyStr1 = _a[0], legalCopyStr2 = _a[1];
    var privacyPolicyLinkText = props.strings.getString('signUpPopDisclaimerPrivacyLink');
    var privacyPolicyLink = props.privacyPolicyUrl !== undefined && props.privacyPolicyUrl !== ''
        ? jsx(Link, { to: props.privacyPolicyUrl, target: '_blank', rel: 'noopener noreferrer', style: { display: 'inline' }, children: privacyPolicyLinkText })
        : privacyPolicyLinkText;
    return jsxs(Typography, { component: "p", fontSize: "small", textAlign: "center", children: [legalCopyStr1, privacyPolicyLink, legalCopyStr2] });
};
var SignMeUpButton = function (props) {
    var isEmailValid = useMemo(function () {
        return emailIsValid(props.customerEmail);
    }, [props.customerEmail]);
    var signUp = function () {
        var _a, _b;
        props.setSigningUp(true);
        subscribeUser(props.customerEmail, props.tenant, props.authorization, props.isAnonymous, {
            mockApis: (_a = props.options) === null || _a === void 0 ? void 0 : _a.mockApis,
            environment: (_b = props.options) === null || _b === void 0 ? void 0 : _b.cucosEnvironment
        }).then(function () {
            props.setSigningUp(false);
            props.setSignedUp(true);
            props.eventTracker.offerPlacementAccepted();
        }).catch(function (error) {
            props.setSigningUp(false);
            props.setSignedUp(true);
            console.error('Subscribing failed:', error);
            props.setSigningError(true);
        });
    };
    return (jsx(Button, { disabled: !isEmailValid || props.signingUp, skin: "primary", onClick: signUp, style: { minWidth: 200 }, children: props.strings.getString('signUpPopCallToActionButton') }));
};
var Content = function (props) {
    var _a = useState(false), signingError = _a[0], setSigningError = _a[1];
    var titleRef = createTypelessRef();
    var src = getImage(props.language, 'w_800');
    var srcSet = [
        "".concat(getImage(props.language, 'w_400'), " 400w"),
        "".concat(getImage(props.language, 'w_800'), " 800w"), // displays about 100% width of viewport
        "".concat(getImage(props.language, 'w_960'), " 1920w") // displays ~50% width of viewport
    ].join(',');
    var signUpConfirmationFunction = function () {
        var expirationDateString = "".concat(dateToLocalizedString(props.expirationDate, props.language));
        return (jsxs(Box, { children: [jsx(Typography, { textAlign: "center", marginY: 5, fontSkin: "title-subsection", role: "heading", ref: titleRef, children: props.strings.getString('signUpConfirmationHeadline') }), jsx(Typography, { textAlign: "center", marginY: 5, fontSkin: "body-standard", children: props.strings.getString('signUpConfirmationSubcopy') }), jsx(Typography, { textAlign: "center", fontSkin: "body-standard-bold", children: props.strings.getString('signUpConfirmationBodyCopy') }), jsx(Typography, { textAlign: "left", fontSkin: "footnote", component: "p", children: props.strings.getString('signUpConfirmationDisclaimer').replace('{{expirationDate}}', expirationDateString) })] }));
    };
    var signingErrorFunction = function () {
        return (jsxs(Box, { children: [jsx(Typography, { textAlign: "center", marginY: 5, fontSkin: "title-subsection", role: "heading", ref: titleRef, children: props.strings.getString('signUpErrorHeadline') }), jsx(Typography, { textAlign: "center", fontSkin: "body-standard-bold", children: props.strings.getString('signUpErrorBodyCopy') })] }));
    };
    return (jsxs(Banner, { height: "short", textBoxHorizontalAlign: "right", children: [jsx(BannerImageContainer, { children: jsx(BannerImage, { children: jsx(FluidImage, { loading: "lazy", src: src, srcSet: srcSet, alt: "" }) }) }), jsxs(BannerTextContainer, { style: { backgroundColor: 'inherit' }, children: [jsxs(Box, { "aria-live": "polite", children: [props.signedUp && !signingError && signUpConfirmationFunction(), props.signedUp && signingError && signingErrorFunction()] }), !props.signedUp && jsx(SignUpOffer, __assign({}, props, { setSigningError: setSigningError }))] })] }));
};

var urlServiceBaseUrl = 'https://url.prod.merch.vpsvc.com';
var getPrivacyPolicyUrl = function (tenant, language, requestor) {
    return typedFetch(urlServiceBaseUrl + "/v3/url/".concat(tenant, "/").concat(language, "/static%3Aprivacyandcookiepolicy?requestor=").concat(requestor))
        .then(function (response) { return response.url; });
};
var usePrivacyPolicyUrl = function (tenant, language, requestor) {
    var _a = useState(), privacyPolicyUrl = _a[0], setPrivacyPolicyUrl = _a[1];
    useEffect(function () {
        getPrivacyPolicyUrl(tenant, language, requestor)
            .then(setPrivacyPolicyUrl)
            .catch(function () { return setPrivacyPolicyUrl(''); }); // TODO log an error
    }, [tenant, language, requestor]);
    return privacyPolicyUrl;
};

var parseIntOrUndefined = function (amount) {
    if (amount === undefined) {
        return undefined;
    }
    return parseInt(amount);
};
var discountInfoMapper = function (apiResponse) {
    return {
        amount: parseIntOrUndefined(apiResponse.amount),
        expirationDate: isoDateStringToDate(apiResponse.expirationDate)
    };
};
var useShowEmailSubscriptionComponent = function (props) {
    var result = useShowComponent(discountInfoMapper, props.placementId, props.tenant, props.country, props.language, props.authorization, props.logger, props.environment);
    return {
        show: result.showComponent,
        isAnonymous: result.isAnonymous,
        discountInfo: result.placementInfo
    };
};

var EmailComponent = function (props) {
    var appName = 'EmailSubscriptionComponent';
    var _a = useState(false), show = _a[0], setShow = _a[1];
    var _b = useState(false), signedUp = _b[0], setSignedUp = _b[1];
    var _c = useState(false), signingUp = _c[0], setSigningUp = _c[1];
    var privacyPolicyUrl = usePrivacyPolicyUrl(props.tenant, props.language, appName);
    var strings = new Localizer(props.language);
    var eventTracker = new EventTracker(props.tracking, props.placementData);
    useEffect(function () {
        if (props.show && privacyPolicyUrl !== undefined) {
            setShow(true);
            eventTracker.offerPlacementShown();
        }
    }, [props.show, privacyPolicyUrl]);
    if (!show || props.discountInfo === undefined) {
        return null;
    }
    var discount = props.discountInfo.amount;
    var expirationDate = props.discountInfo.expirationDate;
    var allProps = {
        authorization: props.authorization,
        tenant: props.tenant,
        signingUp: signingUp,
        setSigningUp: setSigningUp,
        signedUp: signedUp,
        setSignedUp: setSignedUp,
        strings: strings,
        discount: discount,
        language: props.language,
        expirationDate: expirationDate,
        eventTracker: eventTracker,
        isAnonymous: props.isAnonymous === undefined || props.isAnonymous,
        privacyPolicyUrl: privacyPolicyUrl,
        options: props.options
    };
    return jsx(Content, __assign({}, allProps));
};

export { EmailComponent, PecsEnvironment, createAuthorization, useShowEmailSubscriptionComponent };
//# sourceMappingURL=index.mjs.map
